/* eslint-disable */
import { useState, useEffect, useCallback } from 'react';

const BLUE = '#2e5bff';
const GREEN = '#3ec09a';
const BG = '#191923';
const CARD = '#242432';
const BORDER = '#44445f';
const MUTED = '#babace';
const WARNING = '#ffb400';

const sectionLabel = (num: string, title: string) => (
  <div
    style={{
      position: 'absolute',
      top: '5vh',
      left: '6vw',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    }}>
    <span
      style={{
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: 'clamp(11px, 1.1vw, 16px)',
        fontWeight: 700,
        color: BLUE,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
      }}>
      {num} — {title}
    </span>
  </div>
);

const slideNumber = (current: number, total: number) => (
  <div
    style={{
      position: 'absolute',
      bottom: '3.5vh',
      right: '5vw',
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(10px, 1vw, 14px)',
      color: MUTED,
      letterSpacing: '0.05em',
    }}>
    {current} / {total}
  </div>
);

const accent = (
  <div
    style={{
      position: 'absolute',
      top: 0,
      right: 0,
      width: '35vw',
      height: '35vh',
      background: `radial-gradient(ellipse at top right, ${BLUE}22 0%, transparent 70%)`,
      pointerEvents: 'none',
    }}
  />
);

const bottomAccent = (
  <div
    style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '30vw',
      height: '25vh',
      background: `radial-gradient(ellipse at bottom left, ${GREEN}18 0%, transparent 70%)`,
      pointerEvents: 'none',
    }}
  />
);

const rule = (
  <div
    style={{
      width: '4vw',
      height: '3px',
      background: BLUE,
      borderRadius: '2px',
      marginBottom: '2.5vh',
    }}
  />
);

const greenRule = (
  <div
    style={{
      width: '4vw',
      height: '3px',
      background: GREEN,
      borderRadius: '2px',
      marginBottom: '2.5vh',
    }}
  />
);

const pill = (label: string, color: string, bg: string) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.3em 0.9em',
      borderRadius: '999px',
      background: bg,
      color,
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(10px, 1vw, 13px)',
      fontWeight: 600,
      letterSpacing: '0.03em',
    }}>
    {label}
  </span>
);

const step = (n: string, title: string, desc: string) => (
  <div style={{ display: 'flex', gap: '1.5vw', alignItems: 'flex-start' }}>
    <div
      style={{
        flexShrink: 0,
        width: 'clamp(28px, 3vw, 44px)',
        height: 'clamp(28px, 3vw, 44px)',
        borderRadius: '50%',
        background: BLUE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Space Grotesk', sans-serif",
        fontWeight: 800,
        fontSize: 'clamp(12px, 1.3vw, 18px)',
        color: '#fff',
      }}>
      {n}
    </div>
    <div>
      <div
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 700,
          fontSize: 'clamp(13px, 1.4vw, 20px)',
          color: '#fff',
          marginBottom: '0.4vh',
        }}>
        {title}
      </div>
      <div
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 'clamp(11px, 1.15vw, 16px)',
          color: MUTED,
          lineHeight: 1.5,
        }}>
        {desc}
      </div>
    </div>
  </div>
);

const bullet = (text: string, color = MUTED) => (
  <div style={{ display: 'flex', gap: '1vw', alignItems: 'flex-start' }}>
    <span
      style={{
        color: BLUE,
        fontWeight: 700,
        fontSize: 'clamp(12px, 1.2vw, 17px)',
        marginTop: '0.1em',
        flexShrink: 0,
      }}>
      ›
    </span>
    <span
      style={{
        fontFamily: "'Inter', sans-serif",
        fontSize: 'clamp(12px, 1.2vw, 17px)',
        color,
        lineHeight: 1.55,
      }}>
      {text}
    </span>
  </div>
);

const cardBox = (children: React.ReactNode, extraStyle: React.CSSProperties = {}) => (
  <div
    style={{
      background: CARD,
      border: `1px solid ${BORDER}`,
      borderRadius: '0.75rem',
      padding: '2.5vh 2vw',
      ...extraStyle,
    }}>
    {children}
  </div>
);

const uiRow = (label: string, value: string, highlight = false) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.8vh 0',
      borderBottom: `1px solid ${BORDER}`,
    }}>
    <span
      style={{
        fontFamily: "'Inter', sans-serif",
        fontSize: 'clamp(10px, 1vw, 14px)',
        color: MUTED,
      }}>
      {label}
    </span>
    <span
      style={{
        fontFamily: "'Inter', sans-serif",
        fontSize: 'clamp(10px, 1vw, 14px)',
        color: highlight ? GREEN : '#fff',
        fontWeight: highlight ? 600 : 400,
      }}>
      {value}
    </span>
  </div>
);

const deepLink = (path: string) => {
  try {
    if (window.top && window.top !== window) {
      // running inside an iframe — navigate the parent app
      window.top.location.assign(path);
    } else {
      // running as a first-class component — open in a new tab so the deck stays open
      window.open(path, '_blank', 'noopener,noreferrer');
    }
  } catch {
    window.open(path, '_blank', 'noopener,noreferrer');
  }
};

const linkBtn = (label: string, path: string) => (
  <button
    onClick={() => deepLink(path)}
    style={{
      position: 'absolute',
      bottom: '8vh',
      left: '8vw',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5em',
      background: 'transparent',
      border: `1px solid ${BLUE}55`,
      borderRadius: '999px',
      padding: '0.4em 1.1em',
      fontFamily: "'Inter', sans-serif",
      fontSize: 'clamp(9px, 0.85vw, 12px)',
      color: BLUE,
      cursor: 'pointer',
      letterSpacing: '0.02em',
      zIndex: 10,
    }}
    onMouseEnter={(e) => {
      (e.currentTarget as HTMLButtonElement).style.background = `${BLUE}18`;
    }}
    onMouseLeave={(e) => {
      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
    }}>
    ↗ Open: {label}
  </button>
);

type Slide = {
  id: number;
  render: () => React.ReactNode;
};

const slides: Slide[] = [
  // ─── TITLE ───────────────────────────────────────────────────────────────
  {
    id: 1,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '0 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50vw',
            height: '100%',
            background: `linear-gradient(135deg, ${BLUE}18 0%, ${GREEN}10 60%, transparent 100%)`,
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '12vh',
            right: '6vw',
            width: '28vw',
            height: '28vw',
            borderRadius: '50%',
            border: `1px solid ${BLUE}30`,
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '18vh',
            right: '10vw',
            width: '18vw',
            height: '18vw',
            borderRadius: '50%',
            border: `1px solid ${GREEN}25`,
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '55vw' }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1.1vw, 15px)',
              fontWeight: 700,
              color: BLUE,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2.5vh',
            }}>
            Owner Onboarding Guide
          </div>
          <h1
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(28px, 5.5vw, 80px)',
              fontWeight: 800,
              color: '#fff',
              lineHeight: 1.08,
              letterSpacing: '-0.02em',
              margin: 0,
              marginBottom: '3vh',
            }}>
            Welcome to
            <br />
            <span style={{ color: BLUE }}>SKUSavvy</span>
          </h1>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(13px, 1.4vw, 20px)',
              color: MUTED,
              lineHeight: 1.6,
              maxWidth: '38vw',
              marginBottom: '5vh',
            }}>
            Everything a 3PL owner needs to set up their portal, connect stores, map clients, and
            run billing — from first login to day-to-day operations.
          </p>
          <div style={{ display: 'flex', gap: '1.5vw', alignItems: 'center' }}>
            {pill('11 Sections', `${BLUE}`, `${BLUE}22`)}
            {pill('Step-by-step', GREEN, `${GREEN}22`)}
          </div>
        </div>
        <div
          style={{
            position: 'absolute',
            bottom: '4vh',
            left: '8vw',
            fontFamily: "'Inter', sans-serif",
            fontSize: 'clamp(10px, 0.9vw, 13px)',
            color: `${MUTED}80`,
          }}>
          3PL Owner Portal — Confidential
        </div>
      </div>
    ),
  },

  // ─── SECTION 1: WHAT IS SKUSAVVY ─────────────────────────────────────────
  {
    id: 2,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${BLUE}20 0%, transparent 50%, ${GREEN}15 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: BLUE,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 01
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Welcome &amp; What is SKUSavvy
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 3,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('01', 'What is SKUSavvy')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '2vh',
            }}>
            Your 3PL Operations Hub
          </h2>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(12px, 1.3vw, 18px)',
              color: MUTED,
              lineHeight: 1.6,
              maxWidth: '55vw',
              marginBottom: '5vh',
            }}>
            SKUSavvy is a portal for third-party logistics (3PL) operators. It connects directly to
            your Shopify stores, tracks orders and inventory in real time, and gives you the tools
            to bill clients accurately.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2vw' }}>
            {cardBox(
              <>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(12px, 1.3vw, 18px)',
                    color: '#fff',
                    marginBottom: '1vh',
                  }}>
                  Stores &amp; Orders
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                    lineHeight: 1.5,
                  }}>
                  Connect Shopify stores, sync orders automatically, and review fulfillment activity
                  in one place.
                </div>
              </>,
            )}
            {cardBox(
              <>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(12px, 1.3vw, 18px)',
                    color: '#fff',
                    marginBottom: '1vh',
                  }}>
                  Client Management
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                    lineHeight: 1.5,
                  }}>
                  Add vendors, map their SKUs, manage rate cards, and generate invoices — all per
                  client.
                </div>
              </>,
            )}
            {cardBox(
              <>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(12px, 1.3vw, 18px)',
                    color: '#fff',
                    marginBottom: '1vh',
                  }}>
                  Billing &amp; Ledger
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                    lineHeight: 1.5,
                  }}>
                  Track inbound, outbound, and adjustment fees. View master or per-client ledgers
                  with full audit trails.
                </div>
              </>,
            )}
          </div>
        </div>
        {slideNumber(3, 36)}
      </div>
    ),
  },

  {
    id: 4,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('01', 'What is SKUSavvy')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Who the Portal Is For
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '3vw',
              maxWidth: '80vw',
            }}>
            <div>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.4vw, 20px)',
                  color: GREEN,
                  marginBottom: '1.5vh',
                }}>
                3PL Owners (You)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2vh' }}>
                {bullet('Configure and manage one or more Shopify stores')}
                {bullet('Add clients, assign rate cards, generate invoices')}
                {bullet('Review all inventory, orders, and ASNs')}
                {bullet('Control what data each client can see')}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.4vw, 20px)',
                  color: BLUE,
                  marginBottom: '1.5vh',
                }}>
                Your Clients
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2vh' }}>
                {bullet('Access their own portal with limited visibility')}
                {bullet('View their inventory, orders, and ledger')}
                {bullet('Submit ASNs for inbound shipments')}
                {bullet('Scoped to their mapped SKUs only')}
              </div>
            </div>
          </div>
        </div>
        {slideNumber(4, 36)}
      </div>
    ),
  },

  // ─── SECTION 2: ACCESSING YOUR ACCOUNT ───────────────────────────────────
  {
    id: 5,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${GREEN}18 0%, transparent 50%, ${BLUE}12 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: GREEN,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 02
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Accessing Your Account
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 6,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('02', 'Accessing Your Account')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            First Login
          </h2>
          <div style={{ display: 'flex', gap: '5vw', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2.5vh' }}>
              {step(
                '1',
                'Open the login page',
                'Navigate to your SKUSavvy portal URL provided by your administrator.',
              )}
              {step(
                '2',
                'Enter your credentials',
                'Use the email address and temporary password from your welcome email.',
              )}
              {step(
                '3',
                'Submit the login form',
                'Click Sign In. The portal authenticates via secure HTTP-only cookies — no extra setup needed.',
              )}
            </div>
            <div style={{ flexShrink: 0, width: '32vw' }}>
              {cardBox(
                <>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 16px)',
                      color: '#fff',
                      marginBottom: '2vh',
                    }}>
                    Login Screen
                  </div>
                  <div
                    style={{
                      background: '#12121a',
                      borderRadius: '0.5rem',
                      padding: '2vh 1.5vw',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '1.2vh',
                    }}>
                    <div
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(10px, 0.9vw, 13px)',
                        color: MUTED,
                      }}>
                      Email address
                    </div>
                    <div
                      style={{
                        background: CARD,
                        border: `1px solid ${BORDER}`,
                        borderRadius: '0.4rem',
                        padding: '0.8vh 1vw',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(10px, 0.9vw, 13px)',
                        color: '#fff',
                      }}>
                      owner@yourwarehouse.com
                    </div>
                    <div
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(10px, 0.9vw, 13px)',
                        color: MUTED,
                        marginTop: '0.5vh',
                      }}>
                      Password
                    </div>
                    <div
                      style={{
                        background: CARD,
                        border: `1px solid ${BORDER}`,
                        borderRadius: '0.4rem',
                        padding: '0.8vh 1vw',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(10px, 0.9vw, 13px)',
                        color: MUTED,
                      }}>
                      ••••••••••••
                    </div>
                    <div
                      style={{
                        background: BLUE,
                        borderRadius: '0.4rem',
                        padding: '1vh 1vw',
                        textAlign: 'center',
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 700,
                        fontSize: 'clamp(10px, 1vw, 14px)',
                        color: '#fff',
                        marginTop: '0.5vh',
                      }}>
                      Sign In
                    </div>
                  </div>
                </>,
              )}
            </div>
          </div>
        </div>
        {slideNumber(6, 36)}
      </div>
    ),
  },

  {
    id: 7,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('02', 'Accessing Your Account')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '1.5vh',
            }}>
            Mandatory Password Reset
          </h2>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(12px, 1.2vw, 17px)',
              color: MUTED,
              marginBottom: '3.5vh',
            }}>
            On first login, SKUSavvy redirects you to a password reset screen. This is required and
            cannot be skipped.
          </p>
          <div style={{ display: 'flex', gap: '4vw', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2vh' }}>
              {bullet('Choose a strong, unique password for your account')}
              {bullet('Confirm the new password in the second field')}
              {bullet('After saving, you are redirected to your dashboard automatically')}
              {bullet('Your temporary password is invalidated immediately')}
            </div>
            <div style={{ flexShrink: 0, width: '28vw' }}>
              {cardBox(
                <>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: WARNING,
                      marginBottom: '0.8vh',
                    }}>
                    Password Reset Required
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 0.9vw, 13px)',
                      color: MUTED,
                      marginBottom: '1.5vh',
                    }}>
                    Your temporary password must be changed before continuing.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1vh' }}>
                    <div
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(10px, 0.9vw, 13px)',
                        color: MUTED,
                      }}>
                      New Password
                    </div>
                    <div
                      style={{
                        background: '#12121a',
                        border: `1px solid ${BORDER}`,
                        borderRadius: '0.4rem',
                        padding: '0.7vh 1vw',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(10px, 0.9vw, 13px)',
                        color: MUTED,
                      }}>
                      ••••••••••••••
                    </div>
                    <div
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(10px, 0.9vw, 13px)',
                        color: MUTED,
                      }}>
                      Confirm Password
                    </div>
                    <div
                      style={{
                        background: '#12121a',
                        border: `1px solid ${GREEN}`,
                        borderRadius: '0.4rem',
                        padding: '0.7vh 1vw',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(10px, 0.9vw, 13px)',
                        color: MUTED,
                      }}>
                      ••••••••••••••
                    </div>
                    <div
                      style={{
                        background: BLUE,
                        borderRadius: '0.4rem',
                        padding: '0.9vh 1vw',
                        textAlign: 'center',
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 700,
                        fontSize: 'clamp(10px, 1vw, 14px)',
                        color: '#fff',
                        marginTop: '0.5vh',
                      }}>
                      Save New Password
                    </div>
                  </div>
                </>,
              )}
            </div>
          </div>
        </div>
        {slideNumber(7, 36)}
      </div>
    ),
  },

  {
    id: 8,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('02', 'Accessing Your Account')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Navigating Your Dashboard
          </h2>
          <div style={{ display: 'flex', gap: '4vw' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2vh' }}>
              {cardBox(
                <>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.2vw, 17px)',
                      color: BLUE,
                      marginBottom: '0.8vh',
                    }}>
                    Left Sidebar
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                      lineHeight: 1.5,
                    }}>
                    Navigate between Stores, Clients, Users, Orders, Products, Inventory, ASNs,
                    Purchases, and the Ledger.
                  </div>
                </>,
              )}
              {cardBox(
                <>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.2vw, 17px)',
                      color: GREEN,
                      marginBottom: '0.8vh',
                    }}>
                    Top Header
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                      lineHeight: 1.5,
                    }}>
                    Shows your current location (breadcrumbs), store selector, and profile menu for
                    account settings and logout.
                  </div>
                </>,
              )}
              {cardBox(
                <>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.2vw, 17px)',
                      color: WARNING,
                      marginBottom: '0.8vh',
                    }}>
                    Owner Dashboard
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                      lineHeight: 1.5,
                    }}>
                    Your home view shows a summary of active stores, pending billing, and recent
                    activity across all your clients.
                  </div>
                </>,
              )}
            </div>
            <div style={{ flexShrink: 0, width: '38vw' }}>
              {cardBox(
                <div style={{ display: 'flex', height: '28vh', minHeight: '160px' }}>
                  <div
                    style={{
                      width: '28%',
                      background: '#12121a',
                      borderRadius: '0.4rem 0 0 0.4rem',
                      padding: '1.5vh 1vw',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.8vh',
                    }}>
                    {[
                      'Stores',
                      'Clients',
                      'Orders',
                      'Products',
                      'Inventory',
                      'Purchases',
                      'Ledger',
                    ].map((item) => (
                      <div
                        key={item}
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(8px, 0.8vw, 11px)',
                          color: item === 'Stores' ? BLUE : MUTED,
                          padding: '0.4vh 0.6vw',
                          borderRadius: '0.25rem',
                          background: item === 'Stores' ? `${BLUE}20` : 'transparent',
                        }}>
                        {item}
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      padding: '1.5vh 1.2vw',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '1vh',
                    }}>
                    <div
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 700,
                        fontSize: 'clamp(10px, 1vw, 14px)',
                        color: '#fff',
                      }}>
                      Owner Dashboard
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8vh' }}>
                      {[
                        { label: 'Active Stores', val: '3' },
                        { label: 'Clients', val: '12' },
                        { label: 'Orders Today', val: '47' },
                        { label: 'Pending Invoices', val: '2' },
                      ].map((m) => (
                        <div
                          key={m.label}
                          style={{
                            background: '#12121a',
                            borderRadius: '0.3rem',
                            padding: '0.7vh 0.8vw',
                          }}>
                          <div
                            style={{
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 'clamp(7px, 0.7vw, 10px)',
                              color: MUTED,
                            }}>
                            {m.label}
                          </div>
                          <div
                            style={{
                              fontFamily: "'Space Grotesk', sans-serif",
                              fontWeight: 700,
                              fontSize: 'clamp(11px, 1.1vw, 16px)',
                              color: '#fff',
                            }}>
                            {m.val}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>,
              )}
            </div>
          </div>
        </div>
        {linkBtn('Dashboard', '/')}
        {slideNumber(8, 36)}
      </div>
    ),
  },

  // ─── SECTION 3: STORE SETUP ──────────────────────────────────────────────
  {
    id: 9,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${BLUE}22 0%, transparent 60%, ${GREEN}12 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: BLUE,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 03
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Store Setup
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 10,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('03', 'Store Setup')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '1.5vh',
            }}>
            The Onboarding Wizard
          </h2>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(12px, 1.2vw, 17px)',
              color: MUTED,
              marginBottom: '4vh',
              maxWidth: '55vw',
            }}>
            When you create a new store, SKUSavvy launches a three-step setup wizard. Complete all
            steps before the store goes live.
          </p>
          <div
            style={{ display: 'flex', alignItems: 'flex-start', gap: '0', position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                top: 'calc(1.5vw)',
                left: '1.5vw',
                right: '1.5vw',
                height: '2px',
                background: BORDER,
                zIndex: 0,
              }}
            />
            {[
              { n: '01', label: 'API Key', sub: 'Connect your Shopify store', color: BLUE },
              { n: '02', label: 'Webhooks', sub: 'Enable real-time data sync', color: GREEN },
              {
                n: '03',
                label: 'Client Linking',
                sub: 'Assign clients to this store',
                color: WARNING,
              },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  position: 'relative',
                  zIndex: 1,
                }}>
                <div
                  style={{
                    width: '3vw',
                    height: '3vw',
                    minWidth: '36px',
                    minHeight: '36px',
                    borderRadius: '50%',
                    background: s.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 800,
                    fontSize: 'clamp(11px, 1.1vw, 16px)',
                    color: '#fff',
                    marginBottom: '1.5vh',
                  }}>
                  {s.n}
                </div>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(12px, 1.3vw, 18px)',
                    color: '#fff',
                    marginBottom: '0.5vh',
                  }}>
                  {s.label}
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                  }}>
                  {s.sub}
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '4vh' }}>
            {cardBox(
              <div style={{ display: 'flex', gap: '1vw', alignItems: 'flex-start' }}>
                <span
                  style={{ color: WARNING, fontWeight: 700, fontSize: 'clamp(11px, 1.1vw, 15px)' }}>
                  Note:
                </span>
                <span
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(11px, 1.1vw, 15px)',
                    color: MUTED,
                  }}>
                  An incomplete setup banner appears on the store until all three wizard steps are
                  finished. You can discard an unfinished store setup from the banner if you decide
                  not to proceed.
                </span>
              </div>,
            )}
          </div>
        </div>
        {linkBtn('Stores', '/stores')}
        {slideNumber(10, 36)}
      </div>
    ),
  },

  {
    id: 11,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('03', 'Store Setup')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '1.5vh',
            }}>
            Step 1: Connect Your Store
          </h2>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(12px, 1.2vw, 17px)',
              color: MUTED,
              marginBottom: '3.5vh',
              maxWidth: '55vw',
            }}>
            Stores connect through SKUSavvy — you do not need to visit Shopify's developer settings
            or create a private app. The API key is provided by SKUSavvy as part of the store
            connection process.
          </p>
          <div style={{ display: 'flex', gap: '4vw', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2.5vh' }}>
              {step(
                '1',
                'Your administrator adds the store',
                'The 3PL owner or SKUSavvy admin initiates the store connection from within the platform.',
              )}
              {step(
                '2',
                'SKUSavvy provides the API key',
                "The API key is generated through SKUSavvy's Shopify connection. It will be shared with you directly.",
              )}
              {step(
                '3',
                'Enter the key in the wizard',
                'Paste the API key into the Shopify Admin API Key field and click Validate & Continue.',
              )}
              {step(
                '4',
                'Validation confirms the link',
                'SKUSavvy verifies the connection to your Shopify store and proceeds to the Webhooks step.',
              )}
            </div>
            <div style={{ flexShrink: 0, width: '30vw' }}>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2vh' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: '#fff',
                    }}>
                    Store Setup — Step 1 of 3
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(9px, 0.9vw, 13px)',
                      color: MUTED,
                    }}>
                    Shopify Admin API Key
                  </div>
                  <div
                    style={{
                      background: '#12121a',
                      border: `1px solid ${BORDER}`,
                      borderRadius: '0.4rem',
                      padding: '0.8vh 1vw',
                      fontFamily: "'Fira Code', monospace",
                      fontSize: 'clamp(8px, 0.8vw, 12px)',
                      color: GREEN,
                    }}>
                    shpat_a1b2c3d4e5f6g7h8...
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(8px, 0.8vw, 11px)',
                      color: `${MUTED}90`,
                      lineHeight: 1.4,
                    }}>
                    Provided by SKUSavvy — no Shopify developer account needed.
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: '0.5vh',
                    }}>
                    <div
                      style={{
                        background: `${BORDER}`,
                        borderRadius: '0.3rem',
                        padding: '0.5vh 1vw',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(9px, 0.9vw, 13px)',
                        color: MUTED,
                      }}>
                      Back
                    </div>
                    <div
                      style={{
                        background: BLUE,
                        borderRadius: '0.3rem',
                        padding: '0.5vh 1vw',
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 700,
                        fontSize: 'clamp(9px, 0.9vw, 13px)',
                        color: '#fff',
                      }}>
                      Validate &amp; Continue
                    </div>
                  </div>
                </div>,
              )}
            </div>
          </div>
        </div>
        {linkBtn('Store Setup', '/stores')}
        {slideNumber(11, 36)}
      </div>
    ),
  },

  {
    id: 12,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('03', 'Store Setup')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '2vh',
            }}>
            Step 2: Webhooks &amp; Step 3: Clients
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3vw' }}>
            <div>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vh' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(12px, 1.3vw, 18px)',
                      color: GREEN,
                    }}>
                    Webhooks
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                      lineHeight: 1.5,
                    }}>
                    Webhooks let Shopify push order and fulfillment events to SKUSavvy in real time
                    — without polling.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                    {bullet('Click Register Webhooks in the wizard')}
                    {bullet('SKUSavvy auto-registers all required topics')}
                    {bullet('Verify each hook shows a green checkmark')}
                    {bullet("Webhook logs are visible under the store's Webhook Logs page")}
                  </div>
                </div>,
              )}
            </div>
            <div>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vh' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(12px, 1.3vw, 18px)',
                      color: WARNING,
                    }}>
                    Client Linking
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                      lineHeight: 1.5,
                    }}>
                    Connect existing clients (vendors) to this store so their orders and inventory
                    are tracked correctly.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                    {bullet('Search for existing clients by name or email')}
                    {bullet('Select all clients whose inventory lives in this store')}
                    {bullet('Clients can be added or removed after setup')}
                    {bullet('Click Finish to complete the wizard')}
                  </div>
                </div>,
              )}
            </div>
          </div>
        </div>
        {linkBtn('Store Setup', '/stores')}
        {slideNumber(12, 36)}
      </div>
    ),
  },

  // ─── SECTION 4: CONNECTING SKUSAVVY ──────────────────────────────────────
  {
    id: 13,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${GREEN}20 0%, transparent 50%, ${BLUE}15 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: GREEN,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 04
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Connecting SKUSavvy
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 14,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('04', 'Connecting SKUSavvy')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            How Sync Works
          </h2>
          <div style={{ display: 'flex', gap: '4vw' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2.5vh' }}>
              {cardBox(
                <>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.2vw, 17px)',
                      color: BLUE,
                      marginBottom: '0.8vh',
                    }}>
                    Orders
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                    }}>
                    Shopify pushes each new or updated order to SKUSavvy via webhook. The order
                    appears in your portal within seconds of creation.
                  </div>
                </>,
              )}
              {cardBox(
                <>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.2vw, 17px)',
                      color: GREEN,
                      marginBottom: '0.8vh',
                    }}>
                    Inventory
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                    }}>
                    Inventory levels are captured in daily snapshots. Adjustments (inbound ASNs,
                    pick movements) post in real time.
                  </div>
                </>,
              )}
              {cardBox(
                <>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.2vw, 17px)',
                      color: WARNING,
                      marginBottom: '0.8vh',
                    }}>
                    Purchase Orders
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                    }}>
                    POs sync from the date you configure as the PO Sync Start Date. Only orders
                    placed on or after that date are imported.
                  </div>
                </>,
              )}
            </div>
            <div style={{ flexShrink: 0, width: '30vw' }}>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2vh' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: '#fff',
                      marginBottom: '0.5vh',
                    }}>
                    Store Overview — Status
                  </div>
                  {uiRow('Shopify connection', 'Active', true)}
                  {uiRow('Webhook health', '4 / 4 registered', true)}
                  {uiRow('Last order sync', '2 minutes ago')}
                  {uiRow('Inventory snapshot', 'Today, 06:00 UTC')}
                  {uiRow('PO sync date', '2025-01-01')}
                </div>,
              )}
            </div>
          </div>
        </div>
        {slideNumber(14, 36)}
      </div>
    ),
  },

  {
    id: 15,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('04', 'Connecting SKUSavvy')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '2vh',
            }}>
            PO Sync Date
          </h2>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(12px, 1.2vw, 17px)',
              color: MUTED,
              marginBottom: '3.5vh',
              maxWidth: '55vw',
            }}>
            The PO Sync Date tells SKUSavvy how far back to pull purchase orders from Shopify. Set
            it once when you first connect a store.
          </p>
          <div style={{ display: 'flex', gap: '4vw', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.8vh' }}>
              {bullet('Navigate to Stores → select your store → Overview tab')}
              {bullet('Find the PO Sync Date field under store settings')}
              {bullet('Select the earliest date you want purchase orders imported from')}
              {bullet('SKUSavvy begins importing POs from that date forward')}
              {bullet('Leave it blank to disable PO sync entirely for that store')}
            </div>
            <div style={{ flexShrink: 0, width: '28vw' }}>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1vh' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: '#fff',
                    }}>
                    PO Sync Settings
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 0.9vw, 13px)',
                      color: MUTED,
                    }}>
                    Sync purchase orders from:
                  </div>
                  <div
                    style={{
                      background: '#12121a',
                      border: `1px solid ${BORDER}`,
                      borderRadius: '0.4rem',
                      padding: '0.7vh 1vw',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 0.9vw, 13px)',
                      color: '#fff',
                    }}>
                    2025-01-01
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(9px, 0.85vw, 12px)',
                      color: MUTED,
                      lineHeight: 1.4,
                    }}>
                    Only vendor orders placed on or after this date will sync. Clear to disable PO
                    import.
                  </div>
                  <div
                    style={{
                      background: BLUE,
                      borderRadius: '0.3rem',
                      padding: '0.7vh 1vw',
                      textAlign: 'center',
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(9px, 0.9vw, 13px)',
                      color: '#fff',
                      marginTop: '0.5vh',
                    }}>
                    Save
                  </div>
                </div>,
              )}
            </div>
          </div>
        </div>
        {linkBtn('Store Overview', '/stores')}
        {slideNumber(15, 36)}
      </div>
    ),
  },

  // ─── SECTION 5: MAPPING CHANNELS & DATA ──────────────────────────────────
  {
    id: 16,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${BLUE}18 0%, transparent 55%, ${GREEN}18 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: BLUE,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 05
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Mapping Channels &amp; Data
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 17,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('05', 'Mapping Channels & Data')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '2vh',
            }}>
            Ownership Mapping Panel
          </h2>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(12px, 1.2vw, 17px)',
              color: MUTED,
              marginBottom: '3vh',
              maxWidth: '55vw',
            }}>
            The Ownership Mapping panel links each Shopify product variant to a specific client.
            Mapped products are attributed correctly in orders and billing.
          </p>
          <div style={{ display: 'flex', gap: '4vw' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.8vh' }}>
              {bullet('Access via Stores → your store → Ownership Mapping tab')}
              {bullet('Each row is a product variant with its current owner assignment')}
              {bullet('Use the search and filter bar to find unmapped SKUs quickly')}
              {bullet('Click a row to assign or change the owner client')}
              {bullet('Changes take effect immediately — no save required')}
            </div>
            <div style={{ flexShrink: 0, width: '34vw' }}>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      padding: '0.8vh 0',
                      borderBottom: `1px solid ${BORDER}`,
                    }}>
                    <span
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(8px, 0.8vw, 11px)',
                        color: MUTED,
                        fontWeight: 600,
                      }}>
                      SKU
                    </span>
                    <span
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(8px, 0.8vw, 11px)',
                        color: MUTED,
                        fontWeight: 600,
                      }}>
                      Product
                    </span>
                    <span
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(8px, 0.8vw, 11px)',
                        color: MUTED,
                        fontWeight: 600,
                      }}>
                      Owner
                    </span>
                  </div>
                  {[
                    { sku: 'SKU-001', prod: 'Blue Widget / S', owner: 'Client A', mapped: true },
                    { sku: 'SKU-002', prod: 'Blue Widget / M', owner: '—', mapped: false },
                    { sku: 'SKU-003', prod: 'Red Gadget', owner: 'Client B', mapped: true },
                    { sku: 'SKU-004', prod: 'Black Case', owner: '—', mapped: false },
                  ].map((r) => (
                    <div
                      key={r.sku}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        padding: '0.8vh 0',
                        borderBottom: `1px solid ${BORDER}30`,
                        alignItems: 'center',
                      }}>
                      <span
                        style={{
                          fontFamily: "'Fira Code', monospace",
                          fontSize: 'clamp(8px, 0.8vw, 11px)',
                          color: '#fff',
                        }}>
                        {r.sku}
                      </span>
                      <span
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(8px, 0.8vw, 11px)',
                          color: MUTED,
                        }}>
                        {r.prod}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4vw' }}>
                        <span
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: r.mapped ? GREEN : '#b02b13',
                            display: 'inline-block',
                          }}
                        />
                        <span
                          style={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: 'clamp(8px, 0.8vw, 11px)',
                            color: r.mapped ? GREEN : '#b02b13',
                          }}>
                          {r.owner}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>,
              )}
            </div>
          </div>
        </div>
        {linkBtn('Ownership Mapping', '/stores')}
        {slideNumber(17, 36)}
      </div>
    ),
  },

  {
    id: 18,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('05', 'Mapping Channels & Data')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Mapped vs Unassigned Badges
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '3vw',
              marginBottom: '3vh',
            }}>
            <div
              style={{
                background: CARD,
                border: `1px solid ${GREEN}40`,
                borderRadius: '0.75rem',
                padding: '2.5vh 2vw',
              }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1vw',
                  marginBottom: '1.5vh',
                }}>
                <div
                  style={{
                    padding: '0.3em 1em',
                    borderRadius: '999px',
                    background: `${GREEN}25`,
                    color: GREEN,
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(10px, 1vw, 14px)',
                  }}>
                  Mapped
                </div>
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'clamp(10px, 1vw, 14px)',
                  color: MUTED,
                  lineHeight: 1.55,
                }}>
                The SKU has an owner client assigned. Fulfillment costs for this variant are billed
                to that client. The client can see it in their portal.
              </div>
            </div>
            <div
              style={{
                background: CARD,
                border: `1px solid #b02b1340`,
                borderRadius: '0.75rem',
                padding: '2.5vh 2vw',
              }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1vw',
                  marginBottom: '1.5vh',
                }}>
                <div
                  style={{
                    padding: '0.3em 1em',
                    borderRadius: '999px',
                    background: `#b02b1325`,
                    color: '#e05040',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(10px, 1vw, 14px)',
                  }}>
                  Unassigned
                </div>
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'clamp(10px, 1vw, 14px)',
                  color: MUTED,
                  lineHeight: 1.55,
                }}>
                The SKU has no owner. Orders containing unassigned SKUs will not generate billable
                fee events. Resolve these promptly to avoid missed billing.
              </div>
            </div>
          </div>
          {cardBox(
            <div style={{ display: 'flex', gap: '1vw', alignItems: 'center' }}>
              <span
                style={{
                  color: WARNING,
                  fontWeight: 700,
                  fontSize: 'clamp(11px, 1.1vw, 15px)',
                  flexShrink: 0,
                }}>
                Tip:
              </span>
              <span
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'clamp(11px, 1.1vw, 15px)',
                  color: MUTED,
                }}>
                Enable the <strong style={{ color: '#fff' }}>Assigned toggle</strong> on product and
                inventory tables to show only mapped items. This is the fastest way to confirm all
                active SKUs have owners.
              </span>
            </div>,
          )}
        </div>
        {slideNumber(18, 36)}
      </div>
    ),
  },

  {
    id: 19,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('05', 'Mapping Channels & Data')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Shopify Channels &amp; Show Mapped Only
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3vw' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.8vh' }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: BLUE,
                  marginBottom: '0.5vh',
                }}>
                Shopify Channel Mappings
              </div>
              {bullet(
                'Some clients sell via multiple Shopify sales channels (Online Store, wholesale, B2B)',
              )}
              {bullet('Channel mappings let you link a client to a specific Shopify channel')}
              {bullet('Orders from that channel are automatically attributed to the mapped client')}
              {bullet('Manage channel mappings from the Ownership Mapping panel')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.8vh' }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: GREEN,
                  marginBottom: '0.5vh',
                }}>
                Show Mapped Only
              </div>
              {bullet('A toggle in the Ownership Mapping panel header')}
              {bullet(
                'When ON, all listing pages (orders, products, inventory) filter to mapped items only',
              )}
              {bullet('The setting persists on the backend — it applies to all your sessions')}
              {bullet('Useful for focusing on active clients without clutter from unassigned data')}
            </div>
          </div>
        </div>
        {linkBtn('Ownership Mapping', '/stores')}
        {slideNumber(19, 36)}
      </div>
    ),
  },

  // ─── SECTION 6: COMPANY SETTINGS ─────────────────────────────────────────
  {
    id: 20,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${GREEN}15 0%, transparent 55%, ${BLUE}15 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: GREEN,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 06
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Company Settings
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 21,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('06', 'Company Settings')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Account Profile &amp; Store Settings
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2vw' }}>
            {cardBox(
              <>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(11px, 1.2vw, 17px)',
                    color: BLUE,
                    marginBottom: '1vh',
                  }}>
                  Account Profile
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                    lineHeight: 1.5,
                    marginBottom: '1.5vh',
                  }}>
                  Update your name, email, and password at any time from the profile page.
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                  }}>
                  Access via the top-right profile menu → Account Settings.
                </div>
              </>,
            )}
            {cardBox(
              <>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(11px, 1.2vw, 17px)',
                    color: GREEN,
                    marginBottom: '1vh',
                  }}>
                  Store Identity
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                    lineHeight: 1.5,
                    marginBottom: '1.5vh',
                  }}>
                  Edit the store's display name and the billing "from" email address used when
                  sending invoices to clients.
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                  }}>
                  Access via Stores → your store → Settings tab.
                </div>
              </>,
            )}
            {cardBox(
              <>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(11px, 1.2vw, 17px)',
                    color: WARNING,
                    marginBottom: '1vh',
                  }}>
                  Brand Colors
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                    lineHeight: 1.5,
                    marginBottom: '1.5vh',
                  }}>
                  Set a primary and accent color for the store. These appear in client-facing views
                  and billing documents.
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                  }}>
                  Access via Stores → your store → Brand Settings.
                </div>
              </>,
            )}
          </div>
        </div>
        {linkBtn('Account Settings', '/account')}
        {slideNumber(21, 36)}
      </div>
    ),
  },

  // ─── SECTION 7: RATE CARDS ────────────────────────────────────────────────
  {
    id: 22,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${WARNING}18 0%, transparent 55%, ${BLUE}15 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: WARNING,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 07
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Rate Cards
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 23,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('07', 'Rate Cards')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '2vh',
            }}>
            Creating a Rate Card
          </h2>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(12px, 1.2vw, 17px)',
              color: MUTED,
              marginBottom: '3.5vh',
              maxWidth: '55vw',
            }}>
            Rate cards define what you charge per 3PL service. Each client can be assigned exactly
            one rate card.
          </p>
          <div style={{ display: 'flex', gap: '4vw' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2.5vh' }}>
              {step('1', 'Open Rate Cards', 'Navigate to Stores → your store → Rate Cards tab.')}
              {step(
                '2',
                'Click Add Rate Card',
                "Enter a name (e.g. 'Standard', 'Enterprise') and fill in fee fields.",
              )}
              {step(
                '3',
                'Save and assign',
                'Save the card, then assign it to one or more clients from the Client detail page.',
              )}
            </div>
            <div style={{ flexShrink: 0, width: '32vw' }}>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1vh' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: '#fff',
                    }}>
                    New Rate Card
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(9px, 0.9vw, 12px)',
                      color: MUTED,
                    }}>
                    Name
                  </div>
                  <div
                    style={{
                      background: '#12121a',
                      border: `1px solid ${BORDER}`,
                      borderRadius: '0.3rem',
                      padding: '0.6vh 0.8vw',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(9px, 0.9vw, 12px)',
                      color: '#fff',
                    }}>
                    Standard 3PL
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '0.8vw',
                      marginTop: '0.5vh',
                    }}>
                    <div>
                      <div
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(8px, 0.8vw, 11px)',
                          color: MUTED,
                          marginBottom: '0.4vh',
                        }}>
                        Fulfillment fee
                      </div>
                      <div
                        style={{
                          background: '#12121a',
                          border: `1px solid ${BORDER}`,
                          borderRadius: '0.3rem',
                          padding: '0.6vh 0.6vw',
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(9px, 0.9vw, 12px)',
                          color: '#fff',
                        }}>
                        $2.50
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(8px, 0.8vw, 11px)',
                          color: MUTED,
                          marginBottom: '0.4vh',
                        }}>
                        Return fee
                      </div>
                      <div
                        style={{
                          background: '#12121a',
                          border: `1px solid ${BORDER}`,
                          borderRadius: '0.3rem',
                          padding: '0.6vh 0.6vw',
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(9px, 0.9vw, 12px)',
                          color: '#fff',
                        }}>
                        $3.00
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(8px, 0.8vw, 11px)',
                          color: MUTED,
                          marginBottom: '0.4vh',
                        }}>
                        Receive fee
                      </div>
                      <div
                        style={{
                          background: '#12121a',
                          border: `1px solid ${BORDER}`,
                          borderRadius: '0.3rem',
                          padding: '0.6vh 0.6vw',
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(9px, 0.9vw, 12px)',
                          color: '#fff',
                        }}>
                        $1.25
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(8px, 0.8vw, 11px)',
                          color: MUTED,
                          marginBottom: '0.4vh',
                        }}>
                        Storage (per unit/mo)
                      </div>
                      <div
                        style={{
                          background: '#12121a',
                          border: `1px solid ${BORDER}`,
                          borderRadius: '0.3rem',
                          padding: '0.6vh 0.6vw',
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(9px, 0.9vw, 12px)',
                          color: '#fff',
                        }}>
                        $0.10
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      background: BLUE,
                      borderRadius: '0.3rem',
                      padding: '0.7vh 0.8vw',
                      textAlign: 'center',
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(9px, 0.9vw, 13px)',
                      color: '#fff',
                      marginTop: '0.5vh',
                    }}>
                    Save Rate Card
                  </div>
                </div>,
              )}
            </div>
          </div>
        </div>
        {linkBtn('Rate Cards', '/stores')}
        {slideNumber(23, 36)}
      </div>
    ),
  },

  {
    id: 24,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('07', 'Rate Cards')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Fee Types Explained
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5vw' }}>
            {[
              {
                name: 'Fulfillment Fee',
                color: BLUE,
                desc: 'Charged per outbound shipment line. Applied whenever an order is picked and shipped from your warehouse.',
              },
              {
                name: 'Return Fee',
                color: GREEN,
                desc: 'Charged per returned unit processed. Applied when a customer return is received and inspected.',
              },
              {
                name: 'Receive Fee',
                color: WARNING,
                desc: 'Charged per unit received into inventory (inbound ASN). Applied when new stock arrives and is counted in.',
              },
              {
                name: 'Storage Fee',
                color: MUTED,
                desc: 'Monthly per-unit fee for inventory held in your warehouse. Calculated from end-of-month snapshot quantities.',
              },
            ].map((f) => (
              <div
                key={f.name}
                style={{
                  background: CARD,
                  border: `1px solid ${BORDER}`,
                  borderRadius: '0.75rem',
                  padding: '2vh 1.8vw',
                }}>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(11px, 1.2vw, 17px)',
                    color: f.color,
                    marginBottom: '0.8vh',
                  }}>
                  {f.name}
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                    lineHeight: 1.5,
                  }}>
                  {f.desc}
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '2.5vh' }}>
            {cardBox(
              <div style={{ display: 'flex', gap: '1vw', alignItems: 'center' }}>
                <span
                  style={{
                    color: WARNING,
                    fontWeight: 700,
                    fontSize: 'clamp(11px, 1.1vw, 15px)',
                    flexShrink: 0,
                  }}>
                  Warning:
                </span>
                <span
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(11px, 1.1vw, 15px)',
                    color: MUTED,
                  }}>
                  If all fee fields are $0.00, SKUSavvy displays a warning banner. Confirm this is
                  intentional before assigning the card to a client.
                </span>
              </div>,
            )}
          </div>
        </div>
        {slideNumber(24, 36)}
      </div>
    ),
  },

  // ─── SECTION 8: CLIENT INVOICING ──────────────────────────────────────────
  {
    id: 25,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${BLUE}20 0%, transparent 55%, ${WARNING}15 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: BLUE,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 08
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Client Invoicing
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 26,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('08', 'Client Invoicing')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Client List &amp; Billing Modal
          </h2>
          <div style={{ display: 'flex', gap: '4vw' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.8vh' }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: BLUE,
                  marginBottom: '0.5vh',
                }}>
                Client List
              </div>
              {bullet(
                'Navigate to Clients in the sidebar to see all clients for the selected store',
              )}
              {bullet('Each row shows client name, assigned rate card, and billing status')}
              {bullet(
                'Add a new client with the Add Client button — set their vendor link and sync start date',
              )}
              {bullet('Click a client row to open their detail page with full billing options')}
            </div>
            <div style={{ flexShrink: 0, width: '34vw' }}>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: '#fff',
                      marginBottom: '1.5vh',
                    }}>
                    Clients — Your Store
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      padding: '0.6vh 0',
                      borderBottom: `1px solid ${BORDER}`,
                    }}>
                    {['Client', 'Rate Card', 'Status'].map((h) => (
                      <span
                        key={h}
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(8px, 0.8vw, 11px)',
                          color: MUTED,
                          fontWeight: 600,
                        }}>
                        {h}
                      </span>
                    ))}
                  </div>
                  {[
                    { name: 'Acme Goods Co.', card: 'Standard', status: 'Active' },
                    { name: 'Blue Sky Retail', card: 'Enterprise', status: 'Active' },
                    { name: 'Maple Imports', card: 'Standard', status: 'Pending' },
                  ].map((c) => (
                    <div
                      key={c.name}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr',
                        padding: '0.8vh 0',
                        borderBottom: `1px solid ${BORDER}30`,
                        alignItems: 'center',
                      }}>
                      <span
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(9px, 0.9vw, 12px)',
                          color: '#fff',
                        }}>
                        {c.name}
                      </span>
                      <span
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(9px, 0.9vw, 12px)',
                          color: MUTED,
                        }}>
                        {c.card}
                      </span>
                      <span
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'clamp(9px, 0.9vw, 12px)',
                          color: c.status === 'Active' ? GREEN : WARNING,
                        }}>
                        {c.status}
                      </span>
                    </div>
                  ))}
                </div>,
              )}
            </div>
          </div>
        </div>
        {linkBtn('Clients', '/clients')}
        {slideNumber(26, 36)}
      </div>
    ),
  },

  {
    id: 27,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('08', 'Client Invoicing')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Generating &amp; Sending Invoices
          </h2>
          <div style={{ display: 'flex', gap: '4vw', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2.5vh' }}>
              {step(
                '1',
                'Set a Sync Start Date',
                'In the client billing modal, set the date from which billing events are calculated. Only events on or after this date appear on invoices.',
              )}
              {step(
                '2',
                'Open the Billing Modal',
                'From the client list or client detail page, click Generate Invoice.',
              )}
              {step(
                '3',
                'Review line items',
                'The modal shows a breakdown: fulfillment, return, receive fees, and any adjustments.',
              )}
              {step(
                '4',
                'Send the invoice',
                "Click Send to email the invoice to the client using the store's billing from-address.",
              )}
            </div>
            <div style={{ flexShrink: 0, width: '30vw' }}>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1vh' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: '#fff',
                    }}>
                    Invoice — Acme Goods Co.
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(9px, 0.9vw, 12px)',
                      color: MUTED,
                    }}>
                    Period: June 2025
                  </div>
                  <div
                    style={{
                      borderTop: `1px solid ${BORDER}`,
                      marginTop: '0.5vh',
                      paddingTop: '0.8vh',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5vh',
                    }}>
                    {uiRow('Fulfillment (47 units)', '$117.50')}
                    {uiRow('Returns (3 units)', '$9.00')}
                    {uiRow('Receive (120 units)', '$150.00')}
                    {uiRow('Storage (580 units)', '$58.00')}
                  </div>
                  <div
                    style={{
                      borderTop: `1px solid ${BORDER}`,
                      marginTop: '0.5vh',
                      paddingTop: '0.8vh',
                    }}>
                    {uiRow('Total Due', '$334.50', true)}
                  </div>
                  <div
                    style={{
                      background: BLUE,
                      borderRadius: '0.3rem',
                      padding: '0.7vh 0.8vw',
                      textAlign: 'center',
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(9px, 0.9vw, 13px)',
                      color: '#fff',
                      marginTop: '0.5vh',
                    }}>
                    Send Invoice
                  </div>
                </div>,
              )}
            </div>
          </div>
        </div>
        {linkBtn('Clients', '/clients')}
        {slideNumber(27, 36)}
      </div>
    ),
  },

  // ─── SECTION 9: LEDGER ────────────────────────────────────────────────────
  {
    id: 28,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${GREEN}20 0%, transparent 55%, ${BLUE}12 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: GREEN,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 09
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Viewing the Ledger
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 29,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('09', 'Viewing the Ledger')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Master Ledger vs Per-Client Ledger
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3vw' }}>
            <div
              style={{
                background: CARD,
                border: `1px solid ${GREEN}40`,
                borderRadius: '0.75rem',
                padding: '2.5vh 2vw',
              }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: GREEN,
                  marginBottom: '1.5vh',
                }}>
                Master Ledger
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'clamp(10px, 1vw, 14px)',
                  color: MUTED,
                  lineHeight: 1.5,
                  marginBottom: '1.5vh',
                }}>
                Shows all billing events across every client for the selected store. Use this for a
                complete financial picture.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                {bullet('Navigate to Ledger in the sidebar')}
                {bullet('Filter by client, date range, or event type')}
                {bullet('Collapsible summary panel shows totals by client')}
                {bullet('50 rows per page with standard pagination')}
              </div>
            </div>
            <div
              style={{
                background: CARD,
                border: `1px solid ${BLUE}40`,
                borderRadius: '0.75rem',
                padding: '2.5vh 2vw',
              }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: BLUE,
                  marginBottom: '1.5vh',
                }}>
                Per-Client Ledger
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'clamp(10px, 1vw, 14px)',
                  color: MUTED,
                  lineHeight: 1.5,
                  marginBottom: '1.5vh',
                }}>
                Scoped to a single client. Ideal for invoice review and client-facing billing
                discussions.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                {bullet('Access via Clients → client name → Ledger tab')}
                {bullet('Same filters as the master ledger')}
                {bullet("Summary panel shows this client's totals only")}
                {bullet('Clients see the same view in their own portal')}
              </div>
            </div>
          </div>
        </div>
        {linkBtn('Ledger', '/ledger')}
        {slideNumber(29, 36)}
      </div>
    ),
  },

  {
    id: 30,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('09', 'Viewing the Ledger')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '3.5vh',
            }}>
            Event Types &amp; Fee Status
          </h2>
          <div style={{ display: 'flex', gap: '4vw' }}>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: BLUE,
                  marginBottom: '1.5vh',
                }}>
                Ledger Event Types
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2vh' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1.5vw',
                    padding: '1vh 1.5vw',
                    background: `${BLUE}15`,
                    borderRadius: '0.5rem',
                    border: `1px solid ${BLUE}30`,
                  }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: BLUE,
                      width: '6vw',
                    }}>
                    Outbound
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                    }}>
                    Order fulfilled / shipped from your warehouse
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1.5vw',
                    padding: '1vh 1.5vw',
                    background: `${GREEN}15`,
                    borderRadius: '0.5rem',
                    border: `1px solid ${GREEN}30`,
                  }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: GREEN,
                      width: '6vw',
                    }}>
                    Inbound
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                    }}>
                    ASN received and stock added to inventory
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1.5vw',
                    padding: '1vh 1.5vw',
                    background: `${WARNING}15`,
                    borderRadius: '0.5rem',
                    border: `1px solid ${WARNING}30`,
                  }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: WARNING,
                      width: '6vw',
                    }}>
                    Adjustment
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                    }}>
                    Manual correction to inventory quantity or billing
                  </div>
                </div>
              </div>
            </div>
            <div style={{ flexShrink: 0, width: '30vw' }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: GREEN,
                  marginBottom: '1.5vh',
                }}>
                Fee Status
              </div>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vh' }}>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.8vw',
                        marginBottom: '0.5vh',
                      }}>
                      <div
                        style={{
                          padding: '0.2em 0.8em',
                          borderRadius: '999px',
                          background: `${GREEN}25`,
                          color: GREEN,
                          fontFamily: "'Inter', sans-serif",
                          fontWeight: 600,
                          fontSize: 'clamp(9px, 0.85vw, 12px)',
                        }}>
                        Confirmed
                      </div>
                    </div>
                    <div
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(10px, 0.95vw, 13px)',
                        color: MUTED,
                      }}>
                      Fees calculated from a completed order with a matching rate card. These are
                      final and included in invoices.
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.8vw',
                        marginBottom: '0.5vh',
                      }}>
                      <div
                        style={{
                          padding: '0.2em 0.8em',
                          borderRadius: '999px',
                          background: `${WARNING}25`,
                          color: WARNING,
                          fontFamily: "'Inter', sans-serif",
                          fontWeight: 600,
                          fontSize: 'clamp(9px, 0.85vw, 12px)',
                          fontStyle: 'italic',
                        }}>
                        Estimated
                      </div>
                    </div>
                    <div
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(10px, 0.95vw, 13px)',
                        color: MUTED,
                      }}>
                      Fees projected for orders without a finalized fee record. Shown in italic —
                      subject to change.
                    </div>
                  </div>
                </div>,
              )}
            </div>
          </div>
        </div>
        {linkBtn('Ledger', '/ledger')}
        {slideNumber(30, 36)}
      </div>
    ),
  },

  // ─── SECTION 10: DAY-TO-DAY ──────────────────────────────────────────────
  {
    id: 31,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${BLUE}18 0%, transparent 55%, ${GREEN}18 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: BLUE,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 10
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Day-to-Day Data Management
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 32,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('10', 'Day-to-Day Data Management')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Inventory Snapshots &amp; Purchase Orders
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3vw' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vh' }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: GREEN,
                }}>
                Inventory Snapshots
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'clamp(10px, 1vw, 14px)',
                  color: MUTED,
                  lineHeight: 1.5,
                }}>
                SKUSavvy takes a daily snapshot of all inventory quantities, typically at 06:00 UTC.
                The Inventory page shows today's snapshot by default.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                {bullet("If today's snapshot is missing, a notice appears on the Inventory page")}
                {bullet('Browse previous days from the date picker on the Inventory page')}
                {bullet(
                  'Movements (inbound/outbound/adjustment) post in real time between snapshots',
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vh' }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: BLUE,
                }}>
                Purchase Orders
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'clamp(10px, 1vw, 14px)',
                  color: MUTED,
                  lineHeight: 1.5,
                }}>
                The Purchases page lists all vendor POs synced from Shopify from your configured PO
                Sync Date forward.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                {bullet('Filter by status (open, closed, cancelled) and date range')}
                {bullet('Expand a row to see individual shipments and additional cost details')}
                {bullet('A Mapped badge on each PO line shows owner assignment status')}
                {bullet('Cost amounts in additional costs are in cents — displayed in dollars')}
              </div>
            </div>
          </div>
        </div>
        {linkBtn('Inventory', '/inventory')}
        {slideNumber(32, 36)}
      </div>
    ),
  },

  {
    id: 33,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('10', 'Day-to-Day Data Management')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            ASN Management &amp; Webhook Logs
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3vw' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vh' }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: WARNING,
                }}>
                ASN Management
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'clamp(10px, 1vw, 14px)',
                  color: MUTED,
                  lineHeight: 1.5,
                }}>
                Advance Shipping Notices (ASNs) are submitted by clients to notify you of incoming
                inventory. You approve or reject them from the ASNs page.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                {bullet('Clients submit ASNs from their portal; you receive them in real time')}
                {bullet('Review the SKU list and expected quantities before approving')}
                {bullet('Approved ASNs post inbound movements and update inventory')}
                {bullet('Rejected ASNs are returned to the client with a reason')}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vh' }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: MUTED,
                }}>
                Webhook Logs
              </div>
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'clamp(10px, 1vw, 14px)',
                  color: MUTED,
                  lineHeight: 1.5,
                }}>
                Webhook logs show every event Shopify pushed to SKUSavvy. Use them to diagnose
                missing orders or delayed data.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                {bullet('Access via Stores → your store → Webhook Logs')}
                {bullet('Each log shows the topic, payload preview, status, and timestamp')}
                {bullet(
                  'Failed webhooks are flagged — investigate and re-trigger from Shopify if needed',
                )}
                {bullet('Regularly review for orphaned webhooks (no matching client)')}
              </div>
            </div>
          </div>
        </div>
        {linkBtn('ASNs', '/asn')}
        {slideNumber(33, 36)}
      </div>
    ),
  },

  {
    id: 34,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {sectionLabel('10', 'Day-to-Day Data Management')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Re-sync &amp; Recalculate Tools
          </h2>
          <div style={{ display: 'flex', gap: '4vw' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2.5vh' }}>
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.2vw, 17px)',
                      color: BLUE,
                    }}>
                    Sync Orders
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                      lineHeight: 1.5,
                    }}>
                    Trigger a manual re-pull of orders from Shopify. Useful after a webhook failure
                    or when data appears stale. Accessible from the Store Overview page.
                  </div>
                </div>,
              )}
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.2vw, 17px)',
                      color: GREEN,
                    }}>
                    Refresh an Order
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                      lineHeight: 1.5,
                    }}>
                    On any order's detail page, use Refresh Order to re-fetch data for that specific
                    order from Shopify. Changed fields are highlighted after the refresh.
                  </div>
                </div>,
              )}
              {cardBox(
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8vh' }}>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.2vw, 17px)',
                      color: WARNING,
                    }}>
                    Recalculate Fees
                  </div>
                  <div
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(10px, 1vw, 14px)',
                      color: MUTED,
                      lineHeight: 1.5,
                    }}>
                    If you update a rate card, use the recalculate option on the client's billing
                    modal to recompute fees for any affected period before sending an invoice.
                  </div>
                </div>,
              )}
            </div>
            <div
              style={{
                flexShrink: 0,
                width: '28vw',
                display: 'flex',
                flexDirection: 'column',
                gap: '2vh',
              }}>
              {cardBox(
                <div>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: 'clamp(11px, 1.1vw, 15px)',
                      color: '#fff',
                      marginBottom: '1.2vh',
                    }}>
                    When to use each tool
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1vh' }}>
                    {[
                      { tool: 'Sync Orders', when: 'Orders missing after a webhook gap' },
                      { tool: 'Refresh Order', when: 'Single order shows wrong data' },
                      { tool: 'Recalculate Fees', when: 'Rate card changed mid-period' },
                    ].map((t) => (
                      <div key={t.tool}>
                        <div
                          style={{
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 600,
                            fontSize: 'clamp(9px, 0.9vw, 13px)',
                            color: BLUE,
                          }}>
                          {t.tool}
                        </div>
                        <div
                          style={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: 'clamp(9px, 0.9vw, 13px)',
                            color: MUTED,
                          }}>
                          {t.when}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>,
              )}
            </div>
          </div>
        </div>
        {linkBtn('Store Overview', '/stores')}
        {slideNumber(34, 36)}
      </div>
    ),
  },

  // ─── SECTION 11: GETTING HELP ─────────────────────────────────────────────
  {
    id: 35,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${GREEN}18 0%, transparent 55%, ${BLUE}18 100%)`,
          }}
        />
        <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1vw, 14px)',
              fontWeight: 700,
              color: GREEN,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '2vh',
            }}>
            Section 11
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(26px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              margin: 0,
            }}>
            Getting Help
          </h2>
        </div>
      </div>
    ),
  },

  {
    id: 36,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '6vh 8vw',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {accent}
        {bottomAccent}
        {sectionLabel('11', 'Getting Help')}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '4vh' }}>
          {rule}
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px, 3vw, 44px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '4vh',
            }}>
            Support Resources
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '2vw',
              marginBottom: '3vh',
            }}>
            {cardBox(
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1vh' }}>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(11px, 1.2vw, 17px)',
                    color: BLUE,
                  }}>
                  Help Centre
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                    lineHeight: 1.5,
                  }}>
                  Step-by-step guides, video walkthroughs, and FAQs for every part of the portal.
                  Access from the ? icon in the top nav.
                </div>
              </div>,
            )}
            {cardBox(
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1vh' }}>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(11px, 1.2vw, 17px)',
                    color: GREEN,
                  }}>
                  SavvyBot
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                    lineHeight: 1.5,
                  }}>
                  The in-app chat assistant answers common questions instantly. Click the chat
                  bubble in the lower-right corner of any page.
                </div>
              </div>,
            )}
            {cardBox(
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1vh' }}>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 'clamp(11px, 1.2vw, 17px)',
                    color: WARNING,
                  }}>
                  Contact Support
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 'clamp(10px, 1vw, 14px)',
                    color: MUTED,
                    lineHeight: 1.5,
                  }}>
                  For complex issues, open a support ticket directly from the Help Centre. Include
                  your store name and a description of the problem.
                </div>
              </div>,
            )}
          </div>
          {cardBox(
            <div style={{ display: 'flex', gap: '2vw', alignItems: 'center' }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 1.3vw, 18px)',
                  color: '#fff',
                }}>
                Quick reference
              </div>
              <div style={{ display: 'flex', gap: '1.5vw', flexWrap: 'wrap' }}>
                {[
                  { label: 'Store setup', path: 'Stores → wizard' },
                  { label: 'Rate cards', path: 'Stores → Rate Cards' },
                  { label: 'Client billing', path: 'Clients → Generate Invoice' },
                  { label: 'Ledger', path: 'Sidebar → Ledger' },
                ].map((l) => (
                  <div
                    key={l.label}
                    style={{ background: '#12121a', borderRadius: '0.4rem', padding: '0.5vh 1vw' }}>
                    <div
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'clamp(9px, 0.85vw, 12px)',
                        color: BLUE,
                        fontWeight: 600,
                      }}>
                      {l.label}
                    </div>
                    <div
                      style={{
                        fontFamily: "'Fira Code', monospace",
                        fontSize: 'clamp(8px, 0.8vw, 11px)',
                        color: MUTED,
                      }}>
                      {l.path}
                    </div>
                  </div>
                ))}
              </div>
            </div>,
          )}
        </div>
        {linkBtn('Dashboard', '/')}
        {slideNumber(36, 36)}
      </div>
    ),
  },

  // ─── CLOSING SLIDE ────────────────────────────────────────────────────────
  {
    id: 37,
    render: () => (
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${BLUE}28 0%, transparent 50%, ${GREEN}20 100%)`,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '15vh',
            right: '8vw',
            width: '30vw',
            height: '30vw',
            borderRadius: '50%',
            border: `1px solid ${BLUE}30`,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '20vh',
            right: '12vw',
            width: '20vw',
            height: '20vw',
            borderRadius: '50%',
            border: `1px solid ${GREEN}25`,
          }}
        />
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: '60vw' }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(10px, 1.1vw, 15px)',
              fontWeight: 700,
              color: GREEN,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginBottom: '3vh',
            }}>
            You're set
          </div>
          <h1
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(24px, 5vw, 72px)',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              marginBottom: '3vh',
            }}>
            Your portal is ready.
            <br />
            <span style={{ color: BLUE }}>Start with your first store.</span>
          </h1>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(12px, 1.3vw, 18px)',
              color: MUTED,
              lineHeight: 1.6,
              marginBottom: '5vh',
            }}>
            Set up a store, map your clients' SKUs, configure rate cards, and your billing pipeline
            runs automatically. Reach out through the Help Centre whenever you need it.
          </p>
          <div
            style={{
              display: 'flex',
              gap: '1.5vw',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <button
              onClick={() => deepLink('/stores')}
              style={{
                padding: '0.8vh 2.2vw',
                borderRadius: '0.5rem',
                background: BLUE,
                border: 'none',
                color: '#fff',
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: 'clamp(12px, 1.2vw, 17px)',
                cursor: 'pointer',
                letterSpacing: '0.01em',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.opacity = '0.85';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.opacity = '1';
              }}>
              Get started →
            </button>
            {pill('support.skusavvy.com', GREEN, `${GREEN}20`)}
          </div>
        </div>
      </div>
    ),
  },
];

export default function OnboardingDeck() {
  const [current, setCurrent] = useState(0);
  const total = slides.length;

  const goNext = useCallback(() => setCurrent((c) => Math.min(c + 1, total - 1)), [total]);
  const goPrev = useCallback(() => setCurrent((c) => Math.max(c - 1, 0)), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') goNext();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goNext, goPrev]);

  const slide = slides[current];

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: BG,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: BORDER,
          zIndex: 100,
        }}>
        <div
          style={{
            height: '100%',
            background: BLUE,
            width: `${((current + 1) / total) * 100}%`,
            transition: 'width 0.3s ease',
          }}
        />
      </div>

      {/* Slide content */}
      <div style={{ flex: 1, position: 'relative' }}>{slide.render()}</div>

      {/* Navigation bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5vh 4vw',
          background: `${BG}e0`,
          backdropFilter: 'blur(8px)',
          borderTop: `1px solid ${BORDER}`,
          zIndex: 100,
        }}>
        <div style={{ display: 'flex', gap: '1.5vw', alignItems: 'center' }}>
          <button
            onClick={goPrev}
            disabled={current === 0}
            style={{
              padding: '0.6vh 1.2vw',
              borderRadius: '0.4rem',
              background: current === 0 ? `${BORDER}40` : CARD,
              border: `1px solid ${BORDER}`,
              color: current === 0 ? `${MUTED}60` : '#fff',
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: 'clamp(10px, 0.9vw, 13px)',
              cursor: current === 0 ? 'default' : 'pointer',
              transition: 'background 0.15s',
            }}>
            ← Previous
          </button>
          <button
            onClick={goNext}
            disabled={current === total - 1}
            style={{
              padding: '0.6vh 1.2vw',
              borderRadius: '0.4rem',
              background: current === total - 1 ? `${BORDER}40` : BLUE,
              border: `1px solid ${current === total - 1 ? BORDER : BLUE}`,
              color: current === total - 1 ? `${MUTED}60` : '#fff',
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: 'clamp(10px, 0.9vw, 13px)',
              cursor: current === total - 1 ? 'default' : 'pointer',
              transition: 'background 0.15s',
            }}>
            Next →
          </button>
        </div>

        {/* Slide dots / quick jump */}
        <div
          style={{
            display: 'flex',
            gap: '0.4vw',
            alignItems: 'center',
            flexWrap: 'wrap',
            maxWidth: '50vw',
            justifyContent: 'center',
          }}>
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              style={{
                width: i === current ? '20px' : '6px',
                height: '6px',
                borderRadius: '999px',
                background: i === current ? BLUE : BORDER,
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </div>

        <div
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 'clamp(10px, 0.9vw, 13px)',
            color: MUTED,
          }}>
          <span style={{ color: '#fff', fontWeight: 600 }}>{current + 1}</span> / {total}
        </div>
      </div>
    </div>
  );
}
